import useDeviceDetails from "../hooks/useDeviceDetails";
import DeviceDataNotices, { sectionCount, sectionHasData, displayDeviceMetrics } from "../components/DeviceDataNotices";
import LayeredAutocompleteSelect from "../components/LayeredAutocompleteSelect";
/**
 * Devices fleet dashboard (MSP Analytics → Devices).
 * Multi-source inventory (RMM platforms + customer Intune); rows keyed by device_key.
 * Source failures and missing integrations have dedicated guidance; tabs are capability-gated.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  LinearProgress,
  Link,
  MenuItem,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AppsIcon from "@mui/icons-material/Apps";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ComputerIcon from "@mui/icons-material/Computer";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MemoryIcon from "@mui/icons-material/Memory";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import SecurityIcon from "@mui/icons-material/Security";
import StorageIcon from "@mui/icons-material/Storage";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { format, isToday, isYesterday } from "date-fns";
import { useFlags } from "launchdarkly-react-client-sdk";
import { Link as RouterLink } from "react-router-dom";
import DevicesApi, {
  DeviceListQuery,
  DeviceListFacets,
  DevicesListResponse,
  DeviceAlertInstance as RmmAlertInstance,
  DeviceRow as RmmDeviceRow,
  DeviceSnapshotDetailsResponse as RmmDeviceSnapshotDetailsResponse,
  DeviceSnapshotDiskMetric as RmmDeviceSnapshotDiskMetric,
  DeviceSnapshotSample as RmmDeviceSnapshotSample,
  DeviceSourceStatus,
} from "../apis/devicesApi";
import DeviceOsIcon, { deviceOsFamilyLabel } from "../components/DeviceOsIcon";
import { isRmmAlertsLdEnabled } from "../utils/rmmAlertsAccess";

const cardSx = { borderRadius: "6px", border: "1px solid #E2E8F0" };

function deviceSourceFailureMessage(source: DeviceSourceStatus): string {
  const message = typeof source.error === "string" ? source.error : source.error?.message || "";
  const status = typeof source.error === "object" ? source.error?.status : null;
  if (status === 403 || /status code 403|access denied|doesn't have access/i.test(message)) {
    return "Access denied. Check the integration account's permissions in the provider.";
  }
  if (status === 401 || /status code 401/i.test(message) || (typeof source.error === "object" && source.error?.code === "AuthenticationError")) {
    return "Authentication failed. Check the integration credentials and reconnect if needed.";
  }
  if (!message || /^Request failed with status code \d+$/i.test(message)) {
    return "Unable to retrieve device data. Check the integration connection and try Refresh again.";
  }
  return message;
}

/** Status filter values for mixed RMM online + Intune compliance. */
const STATUS_FILTER_ALL = "all";
const STATUS_FILTER_ONLINE = "online";
const STATUS_FILTER_OFFLINE = "offline";
const STATUS_FILTER_UNKNOWN = "unknown";
const STATUS_FILTER_COMPLIANCE_PREFIX = "compliance:";

const CUSTOMER_FILTER_ALL = "all";
const PROVIDER_FILTER_ALL = "all";
const OS_FILTER_ALL = "all";
const OS_FILTER_UNKNOWN = "__unknown__";
const APP_LATEST_FILTER_ALL = "all";
const APP_LATEST_FILTER_CURRENT = "current";
const APP_LATEST_FILTER_NEWER = "newer";
const APP_LATEST_FILTER_SYSTEM = "system";
const APP_LATEST_FILTER_NOT_IN_CATALOG = "not_in_catalog";
const APP_LATEST_FILTER_PENDING = "pending";
const APP_LATEST_FILTER_UNKNOWN = "unknown";

/** Keep in sync with softwareVersionStatus().kind. */
type AppLatestKind =
  | typeof APP_LATEST_FILTER_CURRENT
  | typeof APP_LATEST_FILTER_NEWER
  | typeof APP_LATEST_FILTER_SYSTEM
  | typeof APP_LATEST_FILTER_NOT_IN_CATALOG
  | typeof APP_LATEST_FILTER_PENDING
  | typeof APP_LATEST_FILTER_UNKNOWN;
type AppLatestFilter = typeof APP_LATEST_FILTER_ALL | AppLatestKind;

const APP_LATEST_FILTER_LABELS: Record<AppLatestFilter, string> = {
  [APP_LATEST_FILTER_ALL]: "All latest",
  [APP_LATEST_FILTER_CURRENT]: "Current",
  [APP_LATEST_FILTER_NEWER]: "Newer",
  [APP_LATEST_FILTER_SYSTEM]: "System app",
  [APP_LATEST_FILTER_NOT_IN_CATALOG]: "Not in catalog",
  [APP_LATEST_FILTER_PENDING]: "Lookup pending",
  [APP_LATEST_FILTER_UNKNOWN]: "Latest unavailable",
};

const APP_LATEST_FILTER_ORDER: AppLatestKind[] = [
  APP_LATEST_FILTER_CURRENT,
  APP_LATEST_FILTER_NEWER,
  APP_LATEST_FILTER_SYSTEM,
  APP_LATEST_FILTER_NOT_IN_CATALOG,
  APP_LATEST_FILTER_PENDING,
  APP_LATEST_FILTER_UNKNOWN,
];

type SortColumn = "host_name" | "os_family" | "last_seen_ms" | "online" | "customer" | "user" | "provider";
type SortOrder = "asc" | "desc";
type DeviceDetailTab = "overview" | "health" | "alerts" | "applications" | "operations" | "patch" | "ongoing_issues";

interface DeviceAlertHistoryState {
  instances: RmmAlertInstance[];
  lookbackDays: number;
  loading: boolean;
  error: string | null;
  loaded: boolean;
}

interface ApplicationFilterState {
  name: string;
  latest: AppLatestFilter;
}

const headerCellSx = {
  fontWeight: 600,
  bgcolor: "#F8FAFC",
  fontSize: "12px",
  color: "#64748B",
  cursor: "pointer",
  userSelect: "none" as const,
  whiteSpace: "nowrap" as const,
  borderBottom: "1px solid #E2E8F0",
};

const filterCellSx = {
  bgcolor: "#F8FAFC",
  borderBottom: "1px solid #E2E8F0",
  py: 1,
  verticalAlign: "top" as const,
};

const compactFilterSx = {
  "& .MuiInputBase-root": { fontSize: "0.8rem" },
  "& .MuiInputLabel-root": { fontSize: "0.8rem" },
};

const detailTableHeaderCellSx = { ...headerCellSx, cursor: "default" };
const detailPanelSx = {
  border: "1px solid #E2E8F0",
  borderRadius: "6px",
  bgcolor: "#FFFFFF",
  minWidth: 0,
};

function formatLastSeen(ms: number): string {
  if (!ms) return "—";
  try {
    return new Date(ms).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function formatSnapshotAge(ms: number): string {
  if (!ms) return "snapshot unavailable";

  const ageMinutes = Math.max(0, Math.floor((Date.now() - ms) / 60_000));
  if (ageMinutes === 0) return "snapshot just now";
  return `snapshot ${ageMinutes} min${ageMinutes === 1 ? "" : "s"} ago`;
}

function formatAlertTime(ms: number | undefined | null): string {
  if (!ms) return "—";
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return "—";
  if (isToday(date)) return format(date, "p");
  if (isYesterday(date)) return `Yesterday ${format(date, "p")}`;
  if (date.getFullYear() === new Date().getFullYear()) return format(date, "MMM d, p");
  return format(date, "MMM d, yyyy p");
}

function onlineStatusLabel(online: boolean): string {
  return online ? "Online" : "Offline";
}

function onlineStatusColor(online: boolean): "success" | "default" {
  return online ? "success" : "default";
}

function statusFilterOptionLabel(filterKey: string): string {
  if (filterKey === STATUS_FILTER_ONLINE) return "Online";
  if (filterKey === STATUS_FILTER_OFFLINE) return "Offline";
  if (filterKey === STATUS_FILTER_UNKNOWN) return "Unknown";
  if (filterKey.startsWith(STATUS_FILTER_COMPLIANCE_PREFIX)) {
    return formatComplianceLabel(filterKey.slice(STATUS_FILTER_COMPLIANCE_PREFIX.length));
  }
  return filterKey;
}

function customerDisplayName(row: RmmDeviceRow): string {
  const name = row.customer_name?.trim();
  if (name) return name;
  return "—";
}

function deviceUserDisplay(row: RmmDeviceRow): string {
  const label = row.user?.trim();
  if (label) return label;
  return "—";
}

function ongoingIssueCount(row: RmmDeviceRow): number {
  return Number(row.ongoing_issue_count || 0) || 0;
}

function formatIssueType(issueType: string | undefined): string {
  const value = String(issueType || "").trim().toLowerCase();
  if (!value) return "—";
  return value.replaceAll("_", " ");
}

function providerDisplayName(provider: string): string {
  const value = String(provider || "").trim().toLowerCase();
  if (value === "datto") return "Datto";
  if (value === "ninjaone") return "NinjaOne";
  if (value === "intune") return "Intune";
  return value || "device source";
}

/** Snapshot age + counts; never invent a vendor label. */
function formatDevicesStatusLine(
  deviceCount: number,
  counts: Record<string, number>,
  generatedAtMs: number
): string {
  const parts = [`${deviceCount} device${deviceCount === 1 ? "" : "s"}`];
  if (Object.keys(counts).length > 0) {
    parts.push(Object.entries(counts).sort(([a], [b]) => a.localeCompare(b))
      .map(([provider, count]) => `${count} ${providerDisplayName(provider)}`).join(" + "));
  }
  parts.push(formatSnapshotAge(generatedAtMs));
  return parts.join(" · ");
}

function deviceIdentity(row: RmmDeviceRow) {
  return {
    sourceType: String(row.source_type || "").trim(),
    sourceId: row.source_id,
    deviceId: String(row.external_device_id || "").trim(),
  };
}

function rowSupportsTheoAlerts(row: RmmDeviceRow): boolean {
  return Boolean(row.capabilities?.theo_alert_history);
}

function buildTicketUrl(ticketId: string): string {
  return `/supervisor/tickets/${encodeURIComponent(ticketId)}`;
}

function formatTicketMetric(metric: { value?: number | null; unit?: string | null } | null | undefined): string {
  if (!metric || !Number.isFinite(Number(metric.value))) {
    return "";
  }
  const value = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(Number(metric.value));
  const unit = String(metric.unit || "").trim();
  if (!unit) return value;
  return `${value}${unit.startsWith("%") ? "" : " "}${unit}`;
}

function formatBytes(value: unknown): string {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes <= 0) return "—";
  const units = ["B", "KB", "MB", "GB", "TB", "PB"];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: size >= 10 ? 1 : 2 }).format(size)} ${units[unitIndex]}`;
}

function rawRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

/** Always returns an object so callers can read nested fields without null checks. */
function asRecord(value: unknown): Record<string, unknown> {
  return rawRecord(value) || {};
}

function rawArray(value: unknown): Array<Record<string, unknown>> {
  return Array.isArray(value) ? value.filter((item): item is Record<string, unknown> => Boolean(rawRecord(item))) : [];
}

function rawField(source: unknown, ...path: string[]): unknown {
  let cursor: unknown = source;
  for (const key of path) {
    const record = rawRecord(cursor);
    if (!record || !(key in record)) return undefined;
    cursor = record[key];
  }
  return cursor;
}

function rawString(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return "";
}

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function percentOrNull(value: unknown): number | null {
  const parsed = numberOrNull(value);
  if (parsed === null) return null;
  if (parsed >= 0 && parsed <= 1) return Math.round(parsed * 1000) / 10;
  if (parsed < 0 || parsed > 100) return null;
  return Math.round(parsed * 10) / 10;
}

function formatUnknownValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return new Intl.NumberFormat().format(value);
  if (typeof value === "string") return value;
  return JSON.stringify(value);
}

function getSoftwareRows(sample: RmmDeviceSnapshotSample | null): Array<Record<string, unknown>> {
  if (!sectionHasData(sample, "software")) return [];
  const directRows = rawArray(sample?.raw?.software);
  if (directRows.length) return directRows;
  const detected = rawRecord(sample?.raw?.detected_apps);
  const detectedApps = rawArray(detected?.apps);
  if (detectedApps.length) return detectedApps;
  const software = rawRecord(sample?.raw?.software);
  return rawArray(software?.software || software?.data || software?.applications || software?.installedSoftware || software?.items || software?.results || software?.rows);
}

function rowCapabilities(row: RmmDeviceRow, details?: RmmDeviceSnapshotDetailsResponse | null): Record<string, boolean | undefined> {
  return {
    ...(row.capabilities || {}),
    ...(details?.capabilities || {}),
    ...((asRecord(details?.metadata).capabilities as Record<string, boolean> | undefined) || {}),
  };
}

function isIntuneCapabilityRow(row: RmmDeviceRow, details?: RmmDeviceSnapshotDetailsResponse | null): boolean {
  const provider = String(row.provider || details?.source?.provider || details?.provider || "").toLowerCase();
  if (provider === "intune") return true;
  const caps = rowCapabilities(row, details);
  return caps.compliance === true && caps.utilization_history === false;
}

function complianceChipColor(state: string | null | undefined): "success" | "warning" | "error" | "default" {
  const value = String(state || "").trim().toLowerCase();
  if (value === "compliant") return "success";
  if (value === "noncompliant" || value === "non_compliant") return "error";
  if (value === "inGracePeriod" || value === "ingraceperiod" || value === "conflict") return "warning";
  return "default";
}

function formatComplianceLabel(state: string | null | undefined): string {
  const raw = String(state || "").trim();
  if (!raw) return "Unknown";
  const spaced = raw
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const known: Record<string, string> = {
    compliant: "Compliant",
    noncompliant: "Noncompliant",
    ingraceperiod: "In grace period",
    conflict: "Conflict",
    error: "Error",
    unknown: "Unknown",
  };
  const compact = spaced.toLowerCase().replace(/[\s_-]+/g, "");
  if (known[compact]) return known[compact];
  return spaced.replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function formatBoolLabel(value: unknown): string {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return "—";
}

function getOpenAlertRows(sample: RmmDeviceSnapshotSample | null): Array<Record<string, unknown>> {
  const openAlerts = rawRecord(sample?.raw?.open_alerts);
  return rawArray(openAlerts?.alerts);
}

function normalizeAlertText(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

function dedupeAlertsByText(alerts: Array<Record<string, unknown>>): Array<Record<string, unknown> & { duplicate_count?: number }> {
  const byText = new Map<string, Record<string, unknown> & { duplicate_count?: number }>();
  for (const alert of alerts) {
    const title = normalizeAlertText(alertTitle(alert));
    const key = title.toLowerCase();
    const existing = byText.get(key);
    if (!existing) {
      byText.set(key, { ...alert, duplicate_count: 1 });
      continue;
    }
    existing.duplicate_count = Number(existing.duplicate_count || 1) + 1;
    if (alertTimestamp(alert) > alertTimestamp(existing)) {
      byText.set(key, { ...alert, duplicate_count: existing.duplicate_count });
    }
  }
  return Array.from(byText.values()).sort((a, b) => alertTimestamp(b) - alertTimestamp(a));
}

function alertTimestamp(alert: Record<string, unknown>): number {
  const value =
    alert.timestamp ||
    alert.updateTime ||
    alert.updatedAt ||
    alert.createTime ||
    alert.createdAt ||
    rawField(alert, "alertContext", "lastTriggered");
  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return numeric > 10_000_000_000 ? numeric : numeric * 1000;
  }
  const parsed = Date.parse(String(value || ""));
  return Number.isFinite(parsed) ? parsed : 0;
}

function alertTitle(alert: Record<string, unknown>): string {
  return (
    rawString(alert.message) ||
    rawString(alert.subject) ||
    rawString(rawField(alert, "alertContext", "description")) ||
    rawString(alert.diagnostics) ||
    rawString(alert.sourceName) ||
    rawString(rawField(alert, "alertContext", "type")) ||
    rawString(alert.sourceType) ||
    rawString(alert.uid) ||
    rawString(alert.alertUid) ||
    "RMM alert"
  );
}

function alertSubtitle(alert: Record<string, unknown>): string {
  return [
    rawString(rawField(alert, "alertContext", "type")),
    rawString(alert.sourceType),
    rawString(alert.sourceName),
    rawString(alert.priority || alert.severity),
  ]
    .filter(Boolean)
    .join(" · ");
}

function patchStatusLabel(metrics: RmmDeviceSnapshotSample["metrics"]): string {
  const patch = rawRecord(metrics?.patch_management);
  return rawString(patch?.patchStatus || patch?.status) || rawString(metrics?.software_status) || "—";
}

function antivirusLabel(metrics: RmmDeviceSnapshotSample["metrics"]): string {
  const antivirus = rawRecord(metrics?.antivirus);
  return [rawString(antivirus?.antivirusProduct), rawString(antivirus?.antivirusStatus)].filter(Boolean).join(" · ") || "—";
}

function diskLabel(disk: RmmDeviceSnapshotDiskMetric): string {
  return disk.disk_identifier || disk.description || "Disk";
}

function buildDevicePath(row: RmmDeviceRow): string {
  const { sourceType, sourceId, deviceId } = deviceIdentity(row);
  return `/devices/${encodeURIComponent(sourceType)}/${encodeURIComponent(String(sourceId))}/${encodeURIComponent(deviceId)}`;
}

interface AggregateDiskUsage {
  used_percent: number;
  used_bytes: number;
  free_bytes: number;
  total_bytes: number;
  disk_count: number;
}

function diskUsageParts(disk: RmmDeviceSnapshotDiskMetric): { used_bytes: number; free_bytes: number; total_bytes: number } | null {
  const explicitTotal = numberOrNull(disk.size_bytes);
  const explicitFree = numberOrNull(disk.free_bytes);
  const explicitUsed = numberOrNull(disk.used_bytes);
  const usedPercent = percentOrNull(disk.used_percent);

  let totalBytes = explicitTotal;
  let freeBytes = explicitFree;
  let usedBytes = explicitUsed;

  if (usedBytes === null && totalBytes !== null && freeBytes !== null) {
    usedBytes = Math.max(0, totalBytes - freeBytes);
  }
  if (freeBytes === null && totalBytes !== null && usedBytes !== null) {
    freeBytes = Math.max(0, totalBytes - usedBytes);
  }
  if (totalBytes === null && usedBytes !== null && freeBytes !== null) {
    totalBytes = usedBytes + freeBytes;
  }

  if (usedPercent !== null && totalBytes !== null && (usedBytes === null || freeBytes === null)) {
    usedBytes = (totalBytes * usedPercent) / 100;
    freeBytes = Math.max(0, totalBytes - usedBytes);
  }
  if (usedPercent !== null && freeBytes !== null && totalBytes === null && usedPercent < 100) {
    totalBytes = freeBytes / (1 - usedPercent / 100);
    usedBytes = Math.max(0, totalBytes - freeBytes);
  }
  if (usedPercent !== null && usedBytes !== null && totalBytes === null && usedPercent > 0) {
    totalBytes = usedBytes / (usedPercent / 100);
    freeBytes = Math.max(0, totalBytes - usedBytes);
  }

  if (usedBytes === null || freeBytes === null || totalBytes === null || totalBytes <= 0) return null;
  return {
    used_bytes: Math.max(0, usedBytes),
    free_bytes: Math.max(0, freeBytes),
    total_bytes: Math.max(0, totalBytes),
  };
}

function aggregateDiskUsage(metrics: RmmDeviceSnapshotSample["metrics"]): AggregateDiskUsage | null {
  const metricDisks = metrics?.disks;
  const disks: RmmDeviceSnapshotDiskMetric[] = Array.isArray(metricDisks) ? metricDisks : [];
  let usedBytes = 0;
  let freeBytes = 0;
  let diskCount = 0;
  for (const disk of disks) {
    const parts = diskUsageParts(disk);
    if (!parts) continue;
    usedBytes += parts.used_bytes;
    freeBytes += parts.free_bytes;
    diskCount += 1;
  }
  const totalBytes = usedBytes + freeBytes;
  if (!diskCount || totalBytes <= 0) return null;
  return {
    used_percent: Math.round((usedBytes / totalBytes) * 1000) / 10,
    used_bytes: usedBytes,
    free_bytes: freeBytes,
    total_bytes: totalBytes,
    disk_count: diskCount,
  };
}

interface HourlyMetricBucket {
  label: string;
  sampled_at_ms: number | null;
  cpu_used: number | null;
  memory_used: number | null;
  disk_used: number | null;
  alert_count: number | null;
}

function buildHourlyMetricBuckets(samples: RmmDeviceSnapshotSample[]): HourlyMetricBucket[] {
  const now = new Date();
  now.setMinutes(0, 0, 0);
  const firstHourMs = now.getTime() - 23 * 60 * 60 * 1000;
  const buckets = Array.from({ length: 24 }, (_, index) => {
    const start = firstHourMs + index * 60 * 60 * 1000;
    const end = start + 60 * 60 * 1000;
    return { start, end, sample: null as RmmDeviceSnapshotSample | null };
  });

  for (const sample of samples || []) {
    const ts = Number(sample.sampled_at_ms || 0);
    if (!Number.isFinite(ts) || ts < firstHourMs || ts >= firstHourMs + 24 * 60 * 60 * 1000) continue;
    const index = Math.floor((ts - firstHourMs) / (60 * 60 * 1000));
    const bucket = buckets[index];
    if (bucket && (!bucket.sample || Number(bucket.sample.sampled_at_ms || 0) <= ts)) {
      bucket.sample = sample;
    }
  }

  return buckets.map((bucket) => {
    const metrics = displayDeviceMetrics(bucket.sample);
    const openAlertCount = bucket.sample?.sections?.alerts?.state === "ready" ? numberOrNull(metrics.open_alerts?.count) : null;
    return {
      label: format(new Date(bucket.start), "ha"),
      sampled_at_ms: bucket.sample?.sampled_at_ms || null,
      cpu_used: bucket.sample?.sections?.audit?.state === "ready" ? percentOrNull(metrics.cpu?.used_percent) : null,
      memory_used: bucket.sample?.sections?.audit?.state === "ready" ? percentOrNull(metrics.memory?.used_percent) : null,
      disk_used: bucket.sample?.sections?.audit?.state === "ready" ? aggregateDiskUsage(metrics)?.used_percent ?? null : null,
      alert_count: openAlertCount === null ? null : Math.max(0, openAlertCount),
    };
  });
}

function compareVersionish(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
}

function softwareName(software: Record<string, unknown>): string {
  return rawString(software.name || software.displayName || software.applicationName || software.productName);
}

function softwareCurrentVersion(software: Record<string, unknown>): string {
  return rawString(software.version || software.currentVersion || software.installedVersion);
}

function softwareLatestVersion(software: Record<string, unknown>): string {
  return rawString(
    software.latestVersion ||
      software.availableVersion ||
      software.latest_available_version ||
      software.updateVersion ||
      software.update_version
  );
}

function softwareVersionStatus(software: Record<string, unknown>): {
  latest: string;
  label: string;
  color: "success" | "warning" | "default";
  kind: AppLatestKind;
} {
  const current = softwareCurrentVersion(software);
  const latest = softwareLatestVersion(software);
  const status = rawString(software.latestVersionStatus).toLowerCase();
  if (!latest && status === "not_applicable") {
    return { latest: "", label: "System app", color: "default", kind: APP_LATEST_FILTER_SYSTEM };
  }
  if (!latest && status === "not_in_catalog") {
    return { latest: "", label: "Not in catalog", color: "default", kind: APP_LATEST_FILTER_NOT_IN_CATALOG };
  }
  if (!latest && status === "pending") {
    return { latest: "", label: "Lookup pending", color: "default", kind: APP_LATEST_FILTER_PENDING };
  }
  if (!latest) return { latest: "", label: "Latest unavailable", color: "default", kind: APP_LATEST_FILTER_UNKNOWN };
  if (!current) return { latest, label: "Version unknown", color: "default", kind: APP_LATEST_FILTER_UNKNOWN };
  const cmp = compareVersionish(current, latest);
  if (cmp < 0) return { latest, label: `Newer: ${latest}`, color: "warning", kind: APP_LATEST_FILTER_NEWER };
  return { latest, label: "Current", color: "success", kind: APP_LATEST_FILTER_CURRENT };
}

function softwareLatestSortRank(kind: AppLatestKind): number {
  const index = APP_LATEST_FILTER_ORDER.indexOf(kind);
  return index >= 0 ? index : APP_LATEST_FILTER_ORDER.length;
}

/** Build Latest dropdown options from kinds actually present in the Applications rows. */
function buildAppLatestFilterOptions(softwareRows: Array<Record<string, unknown>>): Array<{ value: AppLatestFilter; label: string }> {
  const present = new Set<AppLatestKind>();
  for (const row of softwareRows) {
    present.add(softwareVersionStatus(row).kind);
  }
  const options: Array<{ value: AppLatestFilter; label: string }> = [
    { value: APP_LATEST_FILTER_ALL, label: APP_LATEST_FILTER_LABELS[APP_LATEST_FILTER_ALL] },
  ];
  for (const kind of APP_LATEST_FILTER_ORDER) {
    if (present.has(kind)) {
      options.push({ value: kind, label: APP_LATEST_FILTER_LABELS[kind] });
    }
  }
  return options;
}

function rawCollectionRows(value: unknown): Array<Record<string, unknown>> {
  const direct = rawArray(value);
  if (direct.length) return direct;

  const record = rawRecord(value);
  if (!record) return [];

  const preferredKeys = [
    "patches",
    "installs",
    "softwarePatches",
    "softwarePatchInstalls",
    "osPatches",
    "osPatchInstalls",
    "jobs",
    "activeJobs",
    "activities",
    "activityLog",
    "data",
    "items",
    "results",
    "rows",
  ];
  for (const key of preferredKeys) {
    const rows = rawArray(record[key]);
    if (rows.length) return rows;
  }
  for (const value of Object.values(record)) {
    const rows = rawArray(value);
    if (rows.length) return rows;
  }
  return [];
}

interface RawPatchSection {
  key: string;
  title: string;
  rows: Array<Record<string, unknown>>;
}

function getPatchSections(sample: RmmDeviceSnapshotSample | null): RawPatchSection[] {
  const raw = rawRecord(sample?.raw);
  if (!raw) return [];

  return [
    { key: "os_patches", title: "OS Patches" },
    { key: "os_patch_installs", title: "OS Patch Installs" },
    { key: "software_patches", title: "Software Patches" },
    { key: "software_patch_installs", title: "Software Patch Installs" },
  ]
    .map((section) => ({ ...section, rows: rawCollectionRows(raw[section.key]) }))
    .filter((section) => section.rows.length > 0);
}

function getOperationalSections(sample: RmmDeviceSnapshotSample | null): RawPatchSection[] {
  const raw = rawRecord(sample?.raw);
  if (!raw) return [];

  return [
    { key: "active_jobs", title: "Active Jobs" },
    { key: "activities", title: "Recent Activities" },
    { key: "windows_services", title: "Windows Services" },
    { key: "network_interfaces", title: "Network Interfaces" },
    { key: "processors", title: "Processors" },
  ]
    .map((section) => ({ ...section, rows: rawCollectionRows(raw[section.key]) }))
    .filter((section) => section.rows.length > 0);
}

function collectionRowName(row: Record<string, unknown>, fallback: string): string {
  return (
    rawString(row.name) ||
    rawString(row.displayName) ||
    rawString(row.title) ||
    rawString(row.message) ||
    rawString(row.subject) ||
    rawString(row.interfaceName) ||
    rawString(row.adapterName) ||
    rawString(row.model) ||
    rawString(row.kbArticleId) ||
    rawString(row.patchId) ||
    rawString(row.id) ||
    rawString(row.uid) ||
    fallback
  );
}

function collectionRowStatus(row: Record<string, unknown>): string {
  return (
    rawString(row.status) ||
    rawString(row.jobStatus) ||
    rawString(row.patchStatus) ||
    rawString(row.approvalStatus) ||
    rawString(row.installStatus) ||
    rawString(row.state) ||
    rawString(row.statusCode) ||
    rawString(row.activityType) ||
    rawString(row.type) ||
    rawString(row.severity) ||
    rawString(row.priority) ||
    rawString(row.result) ||
    "—"
  );
}

function collectionRowDate(row: Record<string, unknown>): string {
  const timestamp =
    numberOrNull(row.activityTime) ||
    numberOrNull(row.logonTime) ||
    numberOrNull(row.installedAt) ||
    numberOrNull(row.installTime) ||
    numberOrNull(row.updateTime) ||
    numberOrNull(row.updatedAt) ||
    numberOrNull(row.createTime) ||
    numberOrNull(row.createdAt) ||
    numberOrNull(row.timestamp);
  if (timestamp !== null) return formatAlertTime(timestamp > 10_000_000_000 ? timestamp : timestamp * 1000);

  const textDate =
    rawString(row.activityTime) ||
    rawString(row.logonTime) ||
    rawString(row.installedAt) ||
    rawString(row.installTime) ||
    rawString(row.updateTime) ||
    rawString(row.updatedAt) ||
    rawString(row.createTime) ||
    rawString(row.createdAt) ||
    rawString(row.timestamp);
  const parsed = Date.parse(textDate);
  return Number.isFinite(parsed) ? formatAlertTime(parsed) : "—";
}

function patchRowName(row: Record<string, unknown>): string {
  return collectionRowName(row, "Patch item");
}

function patchRowStatus(row: Record<string, unknown>): string {
  return collectionRowStatus(row);
}

function patchRowDate(row: Record<string, unknown>): string {
  return collectionRowDate(row);
}

function readableFieldLabel(key: string): string {
  return key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function keyValueRows(value: unknown): Array<{ key: string; label: string; value: unknown }> {
  const record = rawRecord(value);
  if (!record) return [];
  return Object.entries(record)
    .filter(([, item]) => item !== null && item !== undefined && item !== "" && !Array.isArray(item) && typeof item !== "object")
    .map(([key, item]) => ({ key, label: readableFieldLabel(key), value: item }));
}

function isEmptyNinjaOnePatchSummary(key: string, value: unknown, provider: string): boolean {
  if (String(provider || "").toLowerCase() !== "ninjaone") return false;
  if (key === "patch_status" && /^no pending patch items$/i.test(rawString(value))) return true;
  const numberValue = numberOrNull(value);
  return numberValue === 0 && /(count|total|pending|failed|rejected|approved|installed|history)/i.test(key);
}

function patchSummaryRows(metrics: RmmDeviceSnapshotSample["metrics"], provider: string): Array<{ key: string; label: string; value: unknown }> {
  const rows: Array<{ key: string; label: string; value: unknown }> = [];
  const seen = new Set<string>();
  const add = (key: string, label: string, value: unknown) => {
    if (value === null || value === undefined || value === "" || value === "—" || seen.has(key)) return;
    if (isEmptyNinjaOnePatchSummary(key, value, provider)) return;
    seen.add(key);
    rows.push({ key, label, value });
  };

  add("patch_status", "Patch Status", patchStatusLabel(metrics));
  add(
    "reboot_required",
    "Reboot Required",
    metrics?.reboot_required === null || metrics?.reboot_required === undefined ? null : metrics.reboot_required ? "Yes" : "No"
  );
  add("software_status", "Software Status", metrics?.software_status || null);
  for (const row of keyValueRows(metrics?.patch_management)) {
    add(`patch_management_${row.key}`, row.label, row.value);
  }
  return rows;
}

function hasPatchData(sample: RmmDeviceSnapshotSample | null, metrics: RmmDeviceSnapshotSample["metrics"], provider: string): boolean {
  return patchSummaryRows(metrics, provider).length > 0 || getPatchSections(sample).length > 0;
}

function hasOperationalData(sample: RmmDeviceSnapshotSample | null): boolean {
  return getOperationalSections(sample).length > 0 || Boolean(rawString(rawRecord(sample?.raw?.last_logged_on_user)?.userName));
}

/**
 * Analytics → Devices fleet table.
 */
interface RmmDevicesProps {
  embedded?: boolean;
}

const RmmDevices: React.FC<RmmDevicesProps> = ({ embedded = false }) => {
  const flags = useFlags();
  const rmmAlertsLdEnabled = isRmmAlertsLdEnabled(flags as Record<string, unknown>);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [devices, setDevices] = useState<RmmDeviceRow[]>([]);
  const [sources, setSources] = useState<DeviceSourceStatus[] | null>(null);
  const [generatedAtMs, setGeneratedAtMs] = useState(0);
  const [deviceCount, setDeviceCount] = useState(0);
  const [rmmAlertsEnabled, setRmmAlertsEnabled] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [filteredCount, setFilteredCount] = useState(0);
  const [providerCounts, setProviderCounts] = useState<Record<string, number>>({});
  const [facets, setFacets] = useState<DeviceListFacets>({ customers: [], providers: [], os_families: [], statuses: [] });
  const [textQuery, setTextQuery] = useState({ hostname: "", user: "" });
  const requestVersion = useRef(0);
  const [inventoryRevision, setInventoryRevision] = useState(0);
  const [expandedDeviceId, setExpandedDeviceId] = useState<string | null>(null);
  const [deviceDetailTabs, setDeviceDetailTabs] = useState<Record<string, DeviceDetailTab>>({});
  const [applicationFilters, setApplicationFilters] = useState<Record<string, ApplicationFilterState>>({});

  const [hostnameFilter, setHostnameFilter] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [osFilter, setOsFilter] = useState(OS_FILTER_ALL);
  const [statusFilter, setStatusFilter] = useState(STATUS_FILTER_ALL);
  const [customerFilter, setCustomerFilter] = useState(CUSTOMER_FILTER_ALL);
  const [providerFilter, setProviderFilter] = useState(PROVIDER_FILTER_ALL);
  const [sortColumn, setSortColumn] = useState<SortColumn>("last_seen_ms");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [deviceAlertHistory, setDeviceAlertHistory] = useState<Record<string, DeviceAlertHistoryState>>({});
  const [pastIssuesExpandedByDevice, setPastIssuesExpandedByDevice] = useState<Record<string, boolean>>({});

  const expandedRow = devices.find(row => row.device_key === expandedDeviceId);
  const expandedIdentity = expandedRow ? deviceIdentity(expandedRow) : null;
  const expandedDetails = useDeviceDetails(expandedIdentity);

  const showOngoingIssuesTab = rmmAlertsLdEnabled && rmmAlertsEnabled;
  const noDeviceSources = sources !== null && sources.length === 0 && deviceCount === 0 && !error;
  const noRmmProvider = sources !== null && !sources.some((source) => source.source_type === "platform");
  const failedSources = sources?.filter((source) => source.status === "error") || [];
  const warningSources = failedSources.filter((source) => source.provider !== "intune");
  const inventoryPending = sources?.some((source) => source.status === "empty") || false;

  useEffect(() => {
    if (textQuery.hostname === hostnameFilter && textQuery.user === userFilter) return;
    const timer = setTimeout(() => {
      setTextQuery({ hostname: hostnameFilter, user: userFilter });
      setPage(0);
    }, 250);
    return () => clearTimeout(timer);
  }, [hostnameFilter, userFilter, textQuery]);

  const query = useMemo<DeviceListQuery>(() => ({
    page, page_size: pageSize, hostname: textQuery.hostname, user: textQuery.user,
    os: osFilter, status: statusFilter, customer: customerFilter, provider: providerFilter,
    sort: sortColumn, order: sortOrder, prioritize_issues: rmmAlertsLdEnabled,
  }), [page, pageSize, textQuery, osFilter, statusFilter, customerFilter, providerFilter, sortColumn, sortOrder, rmmAlertsLdEnabled]);

  const applyInventory = useCallback((data: DevicesListResponse) => {
    setDevices(data.devices);
    setSources(data.sources ?? null);
    setGeneratedAtMs(data.generated_at_ms);
    setDeviceCount(data.device_count);
    setFilteredCount(data.filtered_count);
    setFacets(data.facets);
    setProviderCounts(data.provider_counts);
    setPage(data.page);
    setRmmAlertsEnabled(Boolean(data.rmm_alerts_enabled));
    setError(data.error || null);
  }, []);

  useEffect(() => {
    let active = true;
    const version = ++requestVersion.current;
    const isCurrent = () => active && version === requestVersion.current;
    const loadDevices = async () => {
      setLoading(true);
      setError(null);
      setExpandedDeviceId(null);
      setDeviceAlertHistory({});
      try {
        const data = await DevicesApi.listDevices(query);
        if (isCurrent()) applyInventory(data);
      } catch (e) {
        if (isCurrent()) setError(e instanceof Error ? e.message : "Failed to load devices");
      } finally {
        if (isCurrent()) setLoading(false);
      }
    };
    void loadDevices();
    return () => { active = false; };
  }, [query, applyInventory, inventoryRevision]);

  useEffect(() => {
    if (embedded) {
      return undefined;
    }
    document.title = "Devices — Analytics - Theo";
    return () => {
      document.title = "Theo";
    };
  }, [embedded]);

  const statusFilterOptions = useMemo(() => {
    const order = [STATUS_FILTER_ONLINE, STATUS_FILTER_OFFLINE, STATUS_FILTER_UNKNOWN];
    const statuses = [...facets.statuses].sort((a, b) => {
      const ai = order.indexOf(a), bi = order.indexOf(b);
      return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi) || statusFilterOptionLabel(a).localeCompare(statusFilterOptionLabel(b));
    });
    return [{ value: STATUS_FILTER_ALL, label: "All" }, ...statuses.map(value => ({ value, label: statusFilterOptionLabel(value) }))];
  }, [facets.statuses]);
  const customerFilterOptions = useMemo(() => [{ value: CUSTOMER_FILTER_ALL, label: "All" },
    ...[...facets.customers].sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }))], [facets.customers]);
  const providerFilterOptions = useMemo(() => [{ value: PROVIDER_FILTER_ALL, label: "All" },
    ...facets.providers.map(value => ({ value, label: providerDisplayName(value) }))], [facets.providers]);
  const osFilterOptions = useMemo(() => {
    const order = ["windows", "macos", "linux", "ios", "android"];
    const families = [...facets.os_families].sort((a, b) => {
      const ai = order.indexOf(a), bi = order.indexOf(b);
      return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi) || a.localeCompare(b);
    });
    return [{ value: OS_FILTER_ALL, label: "All" }, ...families.map(value => ({ value,
      label: value === OS_FILTER_UNKNOWN ? "Unknown" : deviceOsFamilyLabel(value) || value }))];
  }, [facets.os_families]);

  useEffect(() => {
    if (sources === null) return;
    const reset = (valid: boolean, value: string, setter: (value: string) => void) => {
      if (value !== "all" && !valid) { setter("all"); setPage(0); }
    };
    reset(facets.customers.some(option => option.value === customerFilter), customerFilter, setCustomerFilter);
    reset(facets.providers.includes(providerFilter), providerFilter, setProviderFilter);
    reset(facets.os_families.includes(osFilter), osFilter, setOsFilter);
    reset(facets.statuses.includes(statusFilter), statusFilter, setStatusFilter);
  }, [sources, facets, customerFilter, providerFilter, osFilter, statusFilter]);

  const handleSort = (column: SortColumn) => {
    setPage(0);
    if (sortColumn === column) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortColumn(column);
    setSortOrder(column === "last_seen_ms" || column === "online" ? "desc" : "asc");
  };

  const renderSortIcon = (column: SortColumn) => {
    if (sortColumn !== column) return null;
    return sortOrder === "asc" ? (
      <ArrowUpwardIcon sx={{ fontSize: 14, ml: 0.5 }} />
    ) : (
      <ArrowDownwardIcon sx={{ fontSize: 14, ml: 0.5 }} />
    );
  };

  const handleRefresh = async () => {
    const version = ++requestVersion.current;
    setRefreshing(true);
    setError(null);
    setExpandedDeviceId(null);
    setDeviceDetailTabs({});
    setApplicationFilters({});
    setDeviceAlertHistory({});
    setPastIssuesExpandedByDevice({});
    try {
      const result = await DevicesApi.refreshDevices(query);
      if (version === requestVersion.current) applyInventory(result);
    } catch (e) {
      if (version === requestVersion.current) setError(e instanceof Error ? e.message : "Failed to refresh devices");
    } finally {
      setRefreshing(false);
      if (version === requestVersion.current) setLoading(false);
      else setInventoryRevision(revision => revision + 1);
    }
  };

  const handleToggleDeviceDetails = async (row: RmmDeviceRow) => {
    if (!row.device_key) return;
    if (expandedDeviceId === row.device_key) {
      setExpandedDeviceId(null);
      return;
    }
    setExpandedDeviceId(row.device_key);
    setDeviceDetailTabs((prev) => ({
      ...prev,
      [row.device_key]: prev[row.device_key] || (isIntuneCapabilityRow(row) ? "overview" : "health"),
    }));
  };

  /** Lazy-load Theo alert history; cached per device_key until page Refresh. */
  const loadDeviceAlertHistory = useCallback(
    async (row: RmmDeviceRow) => {
      if (!row.device_key || !rowSupportsTheoAlerts(row)) return;
      const existing = deviceAlertHistory[row.device_key];
      if (existing?.loaded || existing?.loading) return;

      setDeviceAlertHistory((prev) => ({
        ...prev,
        [row.device_key]: {
          instances: prev[row.device_key]?.instances || [],
          lookbackDays: prev[row.device_key]?.lookbackDays || 30,
          loading: true,
          error: null,
          loaded: Boolean(prev[row.device_key]?.loaded),
        },
      }));

      try {
        const identity = deviceIdentity(row);
        if (!identity.sourceType || !identity.sourceId || !identity.deviceId) {
          throw new Error("Device source identity is incomplete");
        }
        const data = await DevicesApi.listDeviceAlerts(identity);
        setDeviceAlertHistory((prev) => ({
          ...prev,
          [row.device_key]: {
            instances: data.instances || [],
            lookbackDays: data.lookback_days || 30,
            loading: false,
            error: null,
            loaded: true,
          },
        }));
      } catch (e) {
        setDeviceAlertHistory((prev) => ({
          ...prev,
          [row.device_key]: {
            instances: prev[row.device_key]?.instances || [],
            lookbackDays: prev[row.device_key]?.lookbackDays || 30,
            loading: false,
            error: e instanceof Error ? e.message : "Failed to load device issues",
            loaded: Boolean(prev[row.device_key]?.loaded),
          },
        }));
      }
    },
    [deviceAlertHistory]
  );

  const selectDeviceDetailTab = (row: RmmDeviceRow, tab: DeviceDetailTab) => {
    setDeviceDetailTabs((prev) => ({ ...prev, [row.device_key]: tab }));
    if (tab === "ongoing_issues") {
      if (!showOngoingIssuesTab || !rowSupportsTheoAlerts(row)) return;
      void loadDeviceAlertHistory(row);
    }
  };

  const renderTicketTimeline = (instance: RmmAlertInstance) => (
    <Box sx={{ minWidth: 160 }}>
      {(instance.tickets || []).length === 0 ? "—" : null}
      {(instance.tickets || []).map((ticket, index) => {
        const metric = formatTicketMetric(ticket.metric);
        return (
          <Link
            key={ticket.ticket_id}
            href={buildTicketUrl(ticket.ticket_id)}
            target="_blank"
            rel="noreferrer"
            underline="hover"
            onClick={(event) => event.stopPropagation()}
            title={`Open ticket ${ticket.ticket_id}`}
            sx={{
              display: "block",
              py: 0.45,
              borderTop: index === 0 ? "none" : "1px solid #E2E8F0",
              whiteSpace: "nowrap",
            }}
          >
            {formatAlertTime(ticket.created_ms)}
            {metric ? ` (${metric})` : ""}
          </Link>
        );
      })}
    </Box>
  );

  const renderDiskRows = (disks: RmmDeviceSnapshotDiskMetric[]) => {
    const reportedDisks = disks.filter((disk) => percentOrNull(disk.used_percent) !== null);
    if (!reportedDisks.length) {
      return (
        <Typography variant="body2" color="text.secondary">
          No disk usage data reported.
        </Typography>
      );
    }
    return reportedDisks.map((disk, index) => {
      const usedPercent = percentOrNull(disk.used_percent) || 0;
      const progress = Math.max(0, Math.min(100, usedPercent));
      return (
        <Box key={`${diskLabel(disk)}-${index}`} sx={{ py: 1, borderTop: index === 0 ? "none" : "1px solid #E2E8F0" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 0.75 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, overflowWrap: "anywhere" }}>
              {diskLabel(disk)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
              {usedPercent.toFixed(1)}% used
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            color={progress >= 90 ? "error" : progress >= 75 ? "warning" : "primary"}
            sx={{ height: 8, borderRadius: 8, bgcolor: "#E2E8F0" }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
            {formatBytes(disk.free_bytes)} free of {formatBytes(disk.size_bytes)}
          </Typography>
        </Box>
      );
    });
  };

  const renderTickLabels = (buckets: HourlyMetricBucket[], keyPrefix: string) => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        mt: 0.5,
        color: "text.secondary",
      }}
    >
      {[0, 8, 16, 23].map((index) => (
        <Typography
          key={`${keyPrefix}-tick-${index}`}
          variant="caption"
          sx={{ textAlign: index === 0 ? "left" : index === 23 ? "right" : "center" }}
        >
          {buckets[index]?.label}
        </Typography>
      ))}
    </Box>
  );

  const renderHourlyMetricChart = (samples: RmmDeviceSnapshotSample[]) => {
    const buckets = buildHourlyMetricBuckets(samples);
    const metricRows = [
      { key: "cpu_used", label: "CPU used", color: "#2563EB" },
      { key: "memory_used", label: "Memory used", color: "#0F766E" },
      { key: "disk_used", label: "Disk used", color: "#D97706" },
    ] as const;
    const rows = metricRows.filter((row) => buckets.some((bucket) => bucket[row.key] !== null));

    if (!rows.length) {
      return (
        <Typography variant="body2" color="text.secondary">
          {samples[samples.length - 1]?.provider === "datto" ? "No disk audit snapshots have been collected in the last 24 hours." : "No CPU, memory, or disk utilization percentages were reported in the last 24 hours."}
        </Typography>
      );
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {rows.map((row) => {
          const values = buckets.map((bucket) => bucket[row.key]);
          return (
            <Box key={row.key} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "126px minmax(0, 1fr)" }, gap: 1.5 }}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {row.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Hourly % used
                </Typography>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    height: 84,
                    position: "relative",
                    display: "grid",
                    gridTemplateColumns: "repeat(24, minmax(4px, 1fr))",
                    alignItems: "end",
                    gap: "4px",
                    borderBottom: "1px solid #CBD5E1",
                  }}
                >
                  <Box sx={{ position: "absolute", left: 0, right: 0, top: 0, borderTop: "1px solid #DC2626", zIndex: 1 }} />
                  <Typography
                    variant="caption"
                    sx={{
                      position: "absolute",
                      top: 2,
                      right: 4,
                      color: "#DC2626",
                      bgcolor: "#FFFFFF",
                      px: 0.25,
                      fontWeight: 800,
                      lineHeight: 1,
                      zIndex: 2,
                    }}
                  >
                    100%
                  </Typography>
                  {values.map((value, index) => {
                    const height = value === null ? 0 : Math.max(4, Math.min(100, value));
                    const sampledLabel = buckets[index].sampled_at_ms ? formatAlertTime(Number(buckets[index].sampled_at_ms)) : buckets[index].label;
                    return (
                      <Tooltip
                        key={`${row.key}-${buckets[index].label}`}
                        title={`${sampledLabel}: ${value === null ? "not reported" : `${value.toFixed(1)}% used`}`}
                      >
                        <Box
                          sx={{
                            height: `${height}%`,
                            minHeight: value === null ? 4 : undefined,
                            borderRadius: "4px 4px 0 0",
                            bgcolor: value === null ? "#E2E8F0" : row.color,
                            opacity: value === null ? 0.45 : 0.92,
                          }}
                        />
                      </Tooltip>
                    );
                  })}
                </Box>
                {renderTickLabels(buckets, row.key)}
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderHourlyAlertChart = (samples: RmmDeviceSnapshotSample[]) => {
    const buckets = buildHourlyMetricBuckets(samples);
    const values = buckets.map((bucket) => bucket.alert_count);
    const hasReported = values.some((value) => value !== null);
    const maxCount = Math.max(1, ...values.map((value) => value || 0));

    if (!hasReported) {
      return (
        <Typography variant="body2" color="text.secondary">
          Alert counts were not reported in the last 24 hours.
        </Typography>
      );
    }

    return (
      <Box sx={{ minWidth: 0 }}>
        <Box
          sx={{
            height: 96,
            display: "grid",
            gridTemplateColumns: "repeat(24, minmax(4px, 1fr))",
            alignItems: "end",
            gap: "4px",
            borderBottom: "1px solid #CBD5E1",
          }}
        >
          {values.map((value, index) => {
            const height = value === null ? 0 : value === 0 ? 4 : Math.max(8, Math.min(100, (value / maxCount) * 100));
            const sampledLabel = buckets[index].sampled_at_ms ? formatAlertTime(Number(buckets[index].sampled_at_ms)) : buckets[index].label;
            return (
              <Tooltip
                key={`alert-count-${buckets[index].label}`}
                title={`${sampledLabel}: ${value === null ? "not reported" : `${value} open alert${value === 1 ? "" : "s"}`}`}
              >
                <Box
                  sx={{
                    height: `${height}%`,
                    minHeight: value === null ? 4 : undefined,
                    borderRadius: "4px 4px 0 0",
                    bgcolor: value === null ? "#E2E8F0" : value === 0 ? "#CBD5E1" : "#DC2626",
                    opacity: value === null ? 0.45 : value === 0 ? 0.7 : 0.9,
                  }}
                />
              </Tooltip>
            );
          })}
        </Box>
        {renderTickLabels(buckets, "alert-count")}
      </Box>
    );
  };

  const renderHealthSignal = (
    key: string,
    label: string,
    value: React.ReactNode,
    caption: React.ReactNode,
    icon: React.ReactNode,
    accent: string
  ) => (
    <Box key={key} sx={{ p: 1.5, borderRadius: "6px", bgcolor: "#F8FAFC", minWidth: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        <Box sx={{ color: accent, display: "flex", alignItems: "center" }}>{icon}</Box>
        <Typography variant="body2" sx={{ fontWeight: 800 }}>
          {label}
        </Typography>
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.25, overflowWrap: "anywhere" }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", overflowWrap: "anywhere" }}>
        {caption}
      </Typography>
    </Box>
  );

  const renderHealthTab = (latestSample: RmmDeviceSnapshotSample, samples: RmmDeviceSnapshotSample[]) => {
    const metrics = displayDeviceMetrics(latestSample);
    const disks = Array.isArray(metrics.disks) ? metrics.disks : [];
    const cpuUsed = percentOrNull(metrics.cpu?.used_percent);
    const memoryUsed = percentOrNull(metrics.memory?.used_percent);
    const diskUsage = aggregateDiskUsage(metrics);
    const diskUsed = diskUsage?.used_percent ?? null;
    const antivirus = antivirusLabel(metrics);

    const signals: React.ReactNode[] = [];
    if (latestSample.provider === "datto") {
      if (metrics.cpu?.total_cores) signals.push(renderHealthSignal("cpu-spec", "CPU", `${metrics.cpu.total_cores} cores`, "From the device audit", <ComputerIcon fontSize="small" />, "#2563EB"));
      if (metrics.memory?.total_bytes) signals.push(renderHealthSignal("memory-spec", "Memory", formatBytes(metrics.memory.total_bytes), "Installed memory", <MemoryIcon fontSize="small" />, "#0F766E"));
    }
    if (cpuUsed !== null) {
      signals.push(
        renderHealthSignal(
          "cpu",
          "CPU",
          `${cpuUsed.toFixed(1)}% used`,
          rawString(metrics.cpu?.usage_source) || "Reported by RMM",
          <ComputerIcon fontSize="small" />,
          "#2563EB"
        )
      );
    }
    if (memoryUsed !== null) {
      signals.push(
        renderHealthSignal(
          "memory",
          "Memory",
          `${memoryUsed.toFixed(1)}% used`,
          metrics.memory?.total_bytes ? `${formatBytes(metrics.memory.total_bytes)} total` : rawString(metrics.memory?.usage_source) || "Reported by RMM",
          <MemoryIcon fontSize="small" />,
          "#0F766E"
        )
      );
    }
    if (diskUsed !== null) {
      signals.push(
        renderHealthSignal(
          "disk",
          "Disk",
          `${diskUsed.toFixed(1)}% used`,
          `${formatBytes(diskUsage?.used_bytes)} used of ${formatBytes(diskUsage?.total_bytes)} across ${diskUsage?.disk_count} volume${diskUsage?.disk_count === 1 ? "" : "s"}`,
          <StorageIcon fontSize="small" />,
          diskUsed >= 90 ? "#DC2626" : diskUsed >= 75 ? "#D97706" : "#2563EB"
        )
      );
    }
    if (antivirus !== "—") {
      signals.push(
        renderHealthSignal(
          "antivirus",
          "Antivirus",
          "Reported",
          antivirus,
          <SecurityIcon fontSize="small" />,
          "#7C3AED"
        )
      );
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{ ...detailPanelSx, p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <StorageIcon fontSize="small" sx={{ color: "#2563EB" }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                {latestSample.provider === "datto" ? "Disk audit history" : "Reported Utilization"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {latestSample.provider === "datto" ? "Datto hardware audits do not provide live CPU or memory utilization." : "Hourly % used over the last 24 hours"}
              </Typography>
            </Box>
          </Box>
          {renderHourlyMetricChart(samples)}
        </Box>

        <Box sx={{ ...detailPanelSx, p: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5 }}>
            Current Health
          </Typography>
          {signals.length ? (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" }, gap: 1.25 }}>
              {signals}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No current health metrics were reported by the latest snapshot.
            </Typography>
          )}
          <Box sx={{ mt: signals.length ? 2 : 0 }}>{renderDiskRows(disks)}</Box>
        </Box>
      </Box>
    );
  };

  const renderAlertsTab = (
    latestSample: RmmDeviceSnapshotSample,
    samples: RmmDeviceSnapshotSample[],
    providerName: string
  ) => {
    const metrics = displayDeviceMetrics(latestSample);
    const openAlerts = dedupeAlertsByText(getOpenAlertRows(latestSample));

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{ ...detailPanelSx, p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <WarningAmberIcon fontSize="small" sx={{ color: Number(metrics.open_alerts?.count || 0) ? "#DC2626" : "#059669" }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                Alert Volume
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Open alert count by hour over the last 24 hours
              </Typography>
            </Box>
          </Box>
          {renderHourlyAlertChart(samples)}
        </Box>

        <Box sx={{ ...detailPanelSx, p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <WarningAmberIcon fontSize="small" sx={{ color: openAlerts.length ? "#DC2626" : "#059669" }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              Current {providerName} Alerts
            </Typography>
            <Chip size="small" label={`${openAlerts.length}`} variant="outlined" />
          </Box>
          {openAlerts.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {sectionHasData(latestSample, "alerts") ? `No open ${providerName} alerts.` : "Alert data is unavailable."}
            </Typography>
          ) : (
            <Box sx={{ maxHeight: 430, overflowY: "auto", pr: 0.5 }}>
              {openAlerts.map((alert, index) => (
                <Box key={rawString(alert.alertUid || alert.uid) || index} sx={{ py: 1, borderTop: index === 0 ? "none" : "1px solid #E2E8F0" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25, flexWrap: "wrap" }}>
                    <Chip size="small" label={rawString(alert.priority || alert.severity) || "Alert"} color="warning" variant="outlined" />
                    <Typography variant="caption" color="text.secondary">
                      {formatAlertTime(alertTimestamp(alert))}
                    </Typography>
                    {Number(alert.duplicate_count || 0) > 1 ? (
                      <Chip size="small" label={`${alert.duplicate_count} matching`} variant="outlined" />
                    ) : null}
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, overflowWrap: "anywhere" }}>
                    {alertTitle(alert)}
                  </Typography>
                  {alertSubtitle(alert) ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", overflowWrap: "anywhere" }}>
                      {alertSubtitle(alert)}
                    </Typography>
                  ) : null}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  const renderOngoingIssuesTab = (row: RmmDeviceRow) => {
    const history = deviceAlertHistory[row.device_key];
    const alertsLoading = Boolean(history?.loading);
    const alertsError = history?.error || null;
    const lookbackDays = history?.lookbackDays || 30;
    const instances = history?.instances || [];
    const ongoing = instances.filter((instance) => !instance.end_ms);
    const past = instances.filter((instance) => instance.end_ms);
    const pastExpanded = Boolean(pastIssuesExpandedByDevice[row.device_key]);

    return (
      <Box sx={{ ...detailPanelSx, p: 2 }} onClick={(event) => event.stopPropagation()}>
        {alertsError ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {alertsError}
          </Alert>
        ) : null}

        {alertsLoading && !history?.loaded ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress size={28} />
          </Box>
        ) : instances.length === 0 && !alertsError ? (
          <Box sx={{ py: 3, textAlign: "center", color: "text.secondary" }}>
            <Typography variant="body2">No issues for this device in the last {lookbackDays} days.</Typography>
          </Box>
        ) : (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Ongoing issues ({ongoing.length})
              </Typography>
            </Box>
            {ongoing.length === 0 ? (
              <Box sx={{ py: 2, color: "text.secondary" }}>
                <Typography variant="body2">No ongoing issues.</Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={detailTableHeaderCellSx}>Issue type</TableCell>
                      <TableCell sx={detailTableHeaderCellSx}>Start time</TableCell>
                      <TableCell sx={detailTableHeaderCellSx}>Issue timeline</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ongoing.map((instance) => (
                      <TableRow key={`${instance.issue_type}-${instance.start_ms}`}>
                        <TableCell>{instance.issue_type_label || formatIssueType(instance.issue_type)}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{formatAlertTime(instance.start_ms)}</TableCell>
                        <TableCell>{renderTicketTimeline(instance)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Box sx={{ mt: 3 }}>
              <Button
                variant="text"
                size="small"
                onClick={() =>
                  setPastIssuesExpandedByDevice((prev) => ({
                    ...prev,
                    [row.device_key]: !prev[row.device_key],
                  }))
                }
                endIcon={pastExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ px: 0, fontWeight: 700 }}
              >
                Past issues ({past.length})
              </Button>
              <Collapse in={pastExpanded} timeout="auto" unmountOnExit>
                {past.length === 0 ? (
                  <Box sx={{ py: 2, color: "text.secondary" }}>
                    <Typography variant="body2">No past issues in the last {lookbackDays} days.</Typography>
                  </Box>
                ) : (
                  <TableContainer sx={{ mt: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={detailTableHeaderCellSx}>Issue type</TableCell>
                          <TableCell sx={detailTableHeaderCellSx}>Start time</TableCell>
                          <TableCell sx={detailTableHeaderCellSx}>End time</TableCell>
                          <TableCell sx={detailTableHeaderCellSx}>Issue timeline</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {past.map((instance) => (
                          <TableRow key={`${instance.issue_type}-${instance.start_ms}`}>
                            <TableCell>{instance.issue_type_label || formatIssueType(instance.issue_type)}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>{formatAlertTime(instance.start_ms)}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>{instance.end_ms ? formatAlertTime(instance.end_ms) : "—"}</TableCell>
                            <TableCell>{renderTicketTimeline(instance)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Collapse>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const renderApplicationsTab = (row: RmmDeviceRow, softwareRows: Array<Record<string, unknown>>) => {
    const filters = applicationFilters[row.device_key] || { name: "", latest: APP_LATEST_FILTER_ALL };
    const latestFilterOptions = buildAppLatestFilterOptions(softwareRows);
    const activeLatestFilter = latestFilterOptions.some((opt) => opt.value === filters.latest)
      ? filters.latest
      : APP_LATEST_FILTER_ALL;
    const nameNeedle = filters.name.trim().toLowerCase();
    const rows = softwareRows
      .map((software, index) => ({ software, status: softwareVersionStatus(software), index }))
      .filter(({ software, status }) => {
        if (nameNeedle && !softwareName(software).toLowerCase().includes(nameNeedle)) return false;
        if (activeLatestFilter !== APP_LATEST_FILTER_ALL && status.kind !== activeLatestFilter) return false;
        return true;
      })
      .sort((a, b) => softwareLatestSortRank(a.status.kind) - softwareLatestSortRank(b.status.kind) || a.index - b.index);

    const updateFilters = (patch: Partial<ApplicationFilterState>) => {
      setApplicationFilters((prev) => ({
        ...prev,
        [row.device_key]: {
          name: prev[row.device_key]?.name || "",
          latest: prev[row.device_key]?.latest || APP_LATEST_FILTER_ALL,
          ...patch,
        },
      }));
    };

    return (
      <Box sx={{ ...detailPanelSx, p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 1.5, flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AppsIcon fontSize="small" sx={{ color: "#7C3AED" }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              Applications
            </Typography>
            <Chip
              size="small"
              label={rows.length === softwareRows.length ? `${softwareRows.length}` : `${rows.length} of ${softwareRows.length}`}
              variant="outlined"
            />
          </Box>
        </Box>
        {softwareRows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {sectionHasData(expandedDetails.details?.latest_sample, "software") ? "No installed applications were reported." : "Application inventory is unavailable."}
          </Typography>
        ) : (
          <TableContainer sx={{ maxHeight: 520 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...detailTableHeaderCellSx, minWidth: 260 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: "#64748B" }}>
                        Name
                      </Typography>
                      <TextField
                        value={filters.name}
                        onChange={(event) => updateFilters({ name: event.target.value })}
                        size="small"
                        placeholder="Filter name..."
                        sx={{ maxWidth: 320, ...compactFilterSx }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell sx={detailTableHeaderCellSx}>Current</TableCell>
                  <TableCell sx={{ ...detailTableHeaderCellSx, minWidth: 190 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: "#64748B" }}>
                        Latest
                      </Typography>
                      <TextField
                        select
                        value={activeLatestFilter}
                        onChange={(event) => updateFilters({ latest: event.target.value as AppLatestFilter })}
                        size="small"
                        sx={{ maxWidth: 190, ...compactFilterSx }}
                      >
                        {latestFilterOptions.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No applications match the current filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map(({ software, status, index }) => (
                    <TableRow key={`${softwareName(software)}-${softwareCurrentVersion(software)}-${index}`}>
                      <TableCell sx={{ overflowWrap: "anywhere" }}>{softwareName(software) || "—"}</TableCell>
                      <TableCell sx={{ overflowWrap: "anywhere" }}>{softwareCurrentVersion(software) || "—"}</TableCell>
                      <TableCell sx={{ minWidth: 170 }}>
                        <Chip size="small" label={status.label} color={status.color} variant="outlined" />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    );
  };

  const renderOperationsTab = (latestSample: RmmDeviceSnapshotSample, providerName: string) => {
    const sections = getOperationalSections(latestSample);
    const lastLoggedOnUser = rawRecord(latestSample.raw?.last_logged_on_user);
    const summaryRows = [
      {
        key: "last_logged_on_user",
        label: "Last Logged On User",
        value: rawString(lastLoggedOnUser?.userName),
        caption: formatAlertTime(Number(lastLoggedOnUser?.logonTime || 0)),
      },
    ].filter((row) => row.value);

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{ ...detailPanelSx, p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <ComputerIcon fontSize="small" sx={{ color: "#2563EB" }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              {providerName} Inventory
            </Typography>
          </Box>
          {summaryRows.length ? (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" }, gap: 1 }}>
              {summaryRows.map((row) => (
                <Box key={row.key} sx={{ p: 1.25, borderRadius: "6px", bgcolor: "#F8FAFC", minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                    {row.label}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 800, overflowWrap: "anywhere" }}>
                    {row.value}
                  </Typography>
                  {row.caption !== "—" ? (
                    <Typography variant="caption" color="text.secondary">
                      {row.caption}
                    </Typography>
                  ) : null}
                </Box>
              ))}
            </Box>
          ) : null}
        </Box>

        {sections.map((section) => (
          <Box key={section.key} sx={{ ...detailPanelSx, p: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5 }}>
              {section.title} ({section.rows.length})
            </Typography>
            <TableContainer sx={{ maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={detailTableHeaderCellSx}>Name</TableCell>
                    <TableCell sx={detailTableHeaderCellSx}>Status</TableCell>
                    <TableCell sx={detailTableHeaderCellSx}>Time</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {section.rows.map((item, index) => (
                    <TableRow key={`${section.key}-${collectionRowName(item, "Inventory item")}-${index}`}>
                      <TableCell sx={{ overflowWrap: "anywhere" }}>{collectionRowName(item, "Inventory item")}</TableCell>
                      <TableCell sx={{ overflowWrap: "anywhere" }}>{collectionRowStatus(item)}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{collectionRowDate(item)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ))}
      </Box>
    );
  };

  const renderPatchTab = (latestSample: RmmDeviceSnapshotSample, metrics: RmmDeviceSnapshotSample["metrics"], provider: string) => {
    const sections = getPatchSections(latestSample);
    const summaryRows = patchSummaryRows(metrics, provider);

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{ ...detailPanelSx, p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <SecurityIcon fontSize="small" sx={{ color: metrics?.reboot_required ? "#D97706" : "#2563EB" }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              Patch
            </Typography>
          </Box>
          {summaryRows.length ? (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" }, gap: 1 }}>
              {summaryRows.map((row) => (
                <Box key={row.key} sx={{ p: 1.25, borderRadius: "6px", bgcolor: "#F8FAFC", minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                    {row.label}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 800, overflowWrap: "anywhere" }}>
                    {formatUnknownValue(row.value)}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No patch summary fields were reported.
            </Typography>
          )}
        </Box>

        {sections.map((section) => (
          <Box key={section.key} sx={{ ...detailPanelSx, p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                {section.title}
              </Typography>
              <Chip size="small" label={`${section.rows.length}`} variant="outlined" />
            </Box>
            <TableContainer sx={{ maxHeight: 320 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={detailTableHeaderCellSx}>Name</TableCell>
                    <TableCell sx={detailTableHeaderCellSx}>Status</TableCell>
                    <TableCell sx={detailTableHeaderCellSx}>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {section.rows.map((patch, index) => (
                    <TableRow key={`${section.key}-${patchRowName(patch)}-${index}`}>
                      <TableCell sx={{ overflowWrap: "anywhere" }}>{patchRowName(patch)}</TableCell>
                      <TableCell sx={{ overflowWrap: "anywhere" }}>{patchRowStatus(patch)}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{patchRowDate(patch)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ))}
      </Box>
    );
  };

  const renderIntuneOverviewTab = (
    row: RmmDeviceRow,
    details: RmmDeviceSnapshotDetailsResponse | null | undefined,
    latestSample: RmmDeviceSnapshotSample | null
  ) => {
    const metadata = asRecord(details?.metadata);
    const status = asRecord(details?.status);
    const metrics = asRecord(latestSample?.metrics);
    const storageFromMetrics = asRecord(metrics.storage);
    const disk0 = Array.isArray(metrics.disks) && metrics.disks[0] ? asRecord(metrics.disks[0]) : {};
    const storage = Object.keys(storageFromMetrics).length
      ? storageFromMetrics
      : {
          total_bytes: disk0.size_bytes,
          free_bytes: disk0.free_bytes,
          used_bytes: disk0.used_bytes,
          used_percent: disk0.used_percent,
        };
    const hardware = asRecord(metrics.hardware);
    const management = asRecord(metrics.management);
    const primaryUser = asRecord(metrics.primary_user);
    const security = asRecord(metrics.security);
    const exchange = asRecord(metrics.exchange);
    const complianceState =
      rawString(status.compliance_state) ||
      rawString(asRecord(metrics.compliance).state) ||
      rawString(row.compliance_state);
    const lastSeenMs = numberOrNull(row.last_seen_ms) || numberOrNull(status.last_seen_ms) || numberOrNull(metrics.last_seen_ms);
    const memoryBytes =
      numberOrNull(hardware.physical_memory_bytes) ||
      numberOrNull(metadata.physical_memory_bytes) ||
      numberOrNull(asRecord(metrics.memory).total_bytes);
    const diskUsed = percentOrNull(storage.used_percent);
    const facts: Array<{ label: string; value: string; caption?: string }> = [
      {
        label: "Compliance",
        value: formatComplianceLabel(complianceState),
        caption: rawString(metadata.device_category) ? `Category · ${rawString(metadata.device_category)}` : undefined,
      },
      {
        label: "Last check-in",
        value: formatLastSeen(Number(lastSeenMs || 0)),
        caption: "Intune lastSyncDateTime",
      },
      {
        label: "Encryption",
        value: formatBoolLabel(security.is_encrypted ?? metadata.is_encrypted ?? asRecord(metrics.encryption).is_encrypted),
      },
      {
        label: "Ownership",
        value: rawString(metrics.ownership) || rawString(metadata.ownership) || "—",
      },
      {
        label: "OS",
        value:
          [rawString(hardware.operating_system) || rawString(metadata.operating_system), rawString(hardware.os_version) || rawString(metadata.os_version)]
            .filter(Boolean)
            .join(" · ") || "—",
      },
      {
        label: "Hardware",
        value:
          [rawString(hardware.manufacturer) || rawString(metadata.manufacturer), rawString(hardware.model) || rawString(metadata.model)]
            .filter(Boolean)
            .join(" ") || "—",
        caption: rawString(hardware.serial_number) || rawString(metadata.serial_number) || undefined,
      },
      {
        label: "Memory",
        value: memoryBytes ? formatBytes(memoryBytes) : "—",
      },
      {
        label: "Storage",
        value: diskUsed === null ? "—" : `${diskUsed.toFixed(1)}% used`,
        caption:
          numberOrNull(storage.total_bytes) != null
            ? `${formatBytes(storage.used_bytes)} used of ${formatBytes(storage.total_bytes)}`
            : undefined,
      },
      {
        label: "Primary user",
        value:
          rawString(primaryUser.display_name) ||
          rawString(metadata.primary_user_display_name) ||
          rawString(primaryUser.upn) ||
          rawString(metadata.primary_user_upn) ||
          "—",
        caption: rawString(primaryUser.upn) || rawString(metadata.primary_user_upn) || undefined,
      },
      {
        label: "Management",
        value: rawString(management.state) || rawString(metadata.management_state) || "—",
        caption:
          [rawString(management.agent) || rawString(metadata.management_agent), rawString(management.enrollment_type) || rawString(metadata.enrollment_type)]
            .filter(Boolean)
            .join(" · ") || undefined,
      },
      {
        label: "Enrolled",
        value: formatAlertTime(numberOrNull(metadata.enrolled_at_ms)),
      },
      {
        label: "Azure AD device",
        value: rawString(metadata.azure_ad_device_id) || "—",
        caption: metadata.azure_ad_registered == null ? undefined : `Registered · ${formatBoolLabel(metadata.azure_ad_registered)}`,
      },
    ];
    if (rawString(exchange.access_state)) {
      facts.push({
        label: "Exchange access",
        value: rawString(exchange.access_state),
        caption: rawString(exchange.access_state_reason) || undefined,
      });
    }
    if (rawString(security.partner_threat_state)) {
      facts.push({
        label: "Threat state",
        value: rawString(security.partner_threat_state),
      });
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{ ...detailPanelSx, p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <SecurityIcon fontSize="small" sx={{ color: "#2563EB" }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                Intune overview
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Compliance, check-in, hardware, and management from Microsoft Graph (v1.0 + beta apps).
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" }, gap: 1.25 }}>
            {facts.map((fact) =>
              renderHealthSignal(fact.label, fact.label, fact.value, fact.caption || " ", <ComputerIcon fontSize="small" />, "#334155")
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  const renderDeviceDetailPanel = (row: RmmDeviceRow) => {
    const details = expandedDetails.details;
    const loadingDetails = expandedDetails.loading;
    const detailError = expandedDetails.error || expandedDetails.refreshError;
    const samples = details?.samples || [];
    const latestSample = details?.latest_sample || samples[samples.length - 1] || null;
    const metrics = latestSample?.metrics || {};
    const softwareRows = getSoftwareRows(latestSample);
    const providerName = providerDisplayName(row.provider);
    const openAlerts = dedupeAlertsByText(getOpenAlertRows(latestSample));
    const caps = rowCapabilities(row, details);
    const intuneRow = isIntuneCapabilityRow(row, details);
    const tabs: Array<{ value: DeviceDetailTab; label: string; icon: React.ReactElement }> = [];
    if (intuneRow || caps.compliance) {
      tabs.push({ value: "overview", label: "Overview", icon: <ComputerIcon fontSize="small" /> });
    }
    if (caps.utilization_history || caps.live_status) {
      tabs.push({ value: "health", label: "Health", icon: <StorageIcon fontSize="small" /> });
    }
    if (caps.rmm_alerts || (!intuneRow && openAlerts.length > 0)) {
      tabs.push({ value: "alerts", label: `Alerts (${sectionCount(latestSample, "alerts", openAlerts.length)})`, icon: <WarningAmberIcon fontSize="small" /> });
    }
    if (caps.applications !== false && caps.software !== false) {
      tabs.push({ value: "applications", label: `Applications (${sectionCount(latestSample, "software", softwareRows.length)})`, icon: <AppsIcon fontSize="small" /> });
    }
    if (latestSample && hasOperationalData(latestSample) && !intuneRow) {
      tabs.push({ value: "operations", label: "Inventory", icon: <ComputerIcon fontSize="small" /> });
    }
    if (latestSample && hasPatchData(latestSample, metrics, row.provider) && caps.patching !== false) {
      tabs.push({ value: "patch", label: "Patch", icon: <SecurityIcon fontSize="small" /> });
    }
    if (showOngoingIssuesTab && rowSupportsTheoAlerts(row)) {
      const history = deviceAlertHistory[row.device_key];
      const ongoingCount = history?.loaded
        ? history.instances.filter((instance) => !instance.end_ms).length
        : ongoingIssueCount(row);
      tabs.push({
        value: "ongoing_issues",
        label: `Ongoing Issues (${ongoingCount})`,
        icon: <WarningAmberIcon fontSize="small" />,
      });
    }
    if (!tabs.length) {
      tabs.push({ value: "overview", label: "Overview", icon: <ComputerIcon fontSize="small" /> });
    }
    const requestedTab = deviceDetailTabs[row.device_key] || tabs[0].value;
    const activeTab = tabs.some((tab) => tab.value === requestedTab) ? requestedTab : tabs[0].value;

    return (
      <Box sx={{ px: 2, py: 2.25 }}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
          <Button size="small" onClick={() => void expandedDetails.refresh()} disabled={expandedDetails.refreshing}>Refresh device snapshot</Button>
        </Box>
        {details && <DeviceDataNotices details={details} />}
        {detailError ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {detailError}
          </Alert>
        ) : null}
        {loadingDetails && !details ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : !latestSample && !details?.metadata ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No device snapshots have been captured yet.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, overflowWrap: "anywhere" }}>
                {row.host_name || row.external_device_id}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                {customerDisplayName(row)} · {providerName} · Snapshot{" "}
                {formatAlertTime(Number(latestSample?.sampled_at_ms || details?.generated_at_ms || 0))}
              </Typography>
            </Box>

            <Box sx={{ borderBottom: "1px solid #E2E8F0" }} onClick={(event) => event.stopPropagation()}>
              <Tabs
                value={activeTab}
                onChange={(_, value: DeviceDetailTab) => {
                  selectDeviceDetailTab(row, value);
                }}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight: 40,
                  "& .MuiTab-root": { minHeight: 40, textTransform: "none", fontWeight: 700 },
                }}
              >
                {tabs.map((tab) => (
                  <Tab key={tab.value} value={tab.value} icon={tab.icon} iconPosition="start" label={tab.label} />
                ))}
              </Tabs>
            </Box>

            <Box sx={{ pt: 0.5 }}>
              {activeTab === "overview" ? renderIntuneOverviewTab(row, details, latestSample) : null}
              {activeTab === "health" && latestSample ? renderHealthTab(latestSample, samples) : null}
              {activeTab === "alerts" && latestSample ? renderAlertsTab(latestSample, samples, providerName) : null}
              {activeTab === "applications" ? renderApplicationsTab(row, softwareRows) : null}
              {activeTab === "operations" && latestSample ? renderOperationsTab(latestSample, providerName) : null}
              {activeTab === "patch" && latestSample ? renderPatchTab(latestSample, metrics, row.provider) : null}
              {activeTab === "ongoing_issues" ? renderOngoingIssuesTab(row) : null}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flex: "1 1 auto",
        flexDirection: "column",
        minHeight: "100%",
        minWidth: 0,
        width: "100%",
        bgcolor: embedded ? "transparent" : "#F8F9FA",
      }}
    >
      <Box
        sx={{
          p: embedded ? 0 : { xs: 2, sm: 3, md: 4 },
          maxWidth: "1400px",
          width: "100%",
          mx: "auto",
          boxSizing: "border-box",
          flex: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, mb: embedded ? 2 : 3 }}>
          <Box>
            {!embedded ? (
              <Typography variant="h5" sx={{ fontWeight: 700, color: "primary.main", mb: 0.5 }}>
                Devices
              </Typography>
            ) : null}
            <Typography variant="body2" color="text.secondary">
              {formatDevicesStatusLine(deviceCount, providerCounts, generatedAtMs)}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={() => void handleRefresh()}
              disabled={loading || refreshing}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {error ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {!loading && warningSources.length > 0 ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Device data could not be refreshed from {Array.from(new Set(warningSources.map((source) => providerDisplayName(source.provider)))).join(", ")}.
            </Typography>
            {warningSources.map((source) => (
              <Typography component="div" variant="body2" key={`${source.source_type}:${source.source_id}`} sx={{ mt: 0.5 }}>
                <strong>{providerDisplayName(source.provider)}{source.customer_name ? ` (${source.customer_name})` : ""}:</strong>{" "}
                {deviceSourceFailureMessage(source)}
              </Typography>
            ))}
            {devices.length > 0 ? (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Available devices are still shown; some data may be out of date.
              </Typography>
            ) : null}
          </Alert>
        ) : null}

        {!loading && !error && noRmmProvider && !noDeviceSources ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            No RMM provider connected. Add Datto RMM or NinjaOne in{" "}
            <Link component={RouterLink} to="/integrations">Integrations</Link> to include your RMM devices.
            {" "}Microsoft Intune devices from your Microsoft 365 integrations are shown here too.
          </Alert>
        ) : null}

        <Card sx={cardSx}>
          <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
            {loading && sources === null ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress />
              </Box>
            ) : deviceCount === 0 ? (
              <Box sx={{ px: 3, py: 6, textAlign: "center" }}>
                <ComputerIcon sx={{ fontSize: 40, color: "text.secondary", mb: 1 }} />
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {noDeviceSources
                    ? "Connect a device integration"
                    : error || failedSources.length > 0 || sources === null
                      ? "Unable to load devices"
                      : inventoryPending
                        ? "Waiting for device inventory"
                        : "No devices found"}
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  {noDeviceSources
                    ? "Connect Datto RMM or NinjaOne to view and manage your devices here. You can also view Microsoft Intune devices through a Microsoft 365 integration."
                    : error || failedSources.length > 0 || sources === null
                      ? "Check your integration connections, then select Refresh to try again."
                      : inventoryPending
                        ? "Your integration is connected. Select Refresh to load the latest device inventory."
                        : "Your connected integrations have not reported any devices. Check your integration configuration, then select Refresh."}
                </Typography>
                <Button component={RouterLink} to="/integrations" variant={noDeviceSources ? "contained" : "outlined"}>
                  {noDeviceSources ? "Add integration" : "Manage integrations"}
                </Button>
              </Box>
            ) : (
              <>
                <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #E2E8F0" }}>
                  <Typography variant="body2" color="text.secondary">
                    Showing {filteredCount === 0 ? 0 : page * pageSize + 1}–{Math.min((page + 1) * pageSize, filteredCount)} of {filteredCount} matches · {deviceCount} total
                  </Typography>
                </Box>
                {loading && <LinearProgress aria-label="Loading devices" />}
                <TableContainer aria-busy={loading} sx={{ maxHeight: "calc(100vh - 280px)" }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ ...headerCellSx, width: 48, cursor: "default" }} />
                        <TableCell sx={headerCellSx} onClick={() => handleSort("host_name")}>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            Hostname
                            {renderSortIcon("host_name")}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ ...headerCellSx, width: 64 }} onClick={() => handleSort("os_family")}>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            OS
                            {renderSortIcon("os_family")}
                          </Box>
                        </TableCell>
                        <TableCell sx={headerCellSx} onClick={() => handleSort("provider")}>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            Source
                            {renderSortIcon("provider")}
                          </Box>
                        </TableCell>
                        <TableCell sx={headerCellSx} onClick={() => handleSort("last_seen_ms")}>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            Last seen
                            {renderSortIcon("last_seen_ms")}
                          </Box>
                        </TableCell>
                        <TableCell sx={headerCellSx} onClick={() => handleSort("online")}>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            Status
                            {renderSortIcon("online")}
                          </Box>
                        </TableCell>
                        <TableCell sx={headerCellSx} onClick={() => handleSort("customer")}>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            Customer
                            {renderSortIcon("customer")}
                          </Box>
                        </TableCell>
                        <TableCell sx={headerCellSx} onClick={() => handleSort("user")}>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            User
                            {renderSortIcon("user")}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ ...headerCellSx, cursor: "default" }} align="right" />
                      </TableRow>
                      <TableRow>
                        <TableCell sx={filterCellSx} />
                        <TableCell sx={filterCellSx}>
                          <TextField
                            value={hostnameFilter}
                            onChange={(e) => setHostnameFilter(e.target.value)}
                            size="small"
                            placeholder="Filter…"
                            fullWidth
                            sx={compactFilterSx}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </TableCell>
                        <TableCell sx={filterCellSx}>
                          <LayeredAutocompleteSelect
                            label=""
                            value={osFilter}
                            onChange={value => { setOsFilter(value); setPage(0); }}
                            size="small"
                            fullWidth
                            sx={compactFilterSx}
                            onClick={(e) => e.stopPropagation()}
                            inputAriaLabel="Filter devices by operating system"
                            disableClearable
                            options={osFilterOptions}
                          />
                        </TableCell>
                        <TableCell sx={filterCellSx}>
                          <TextField
                            select
                            value={providerFilter}
                            onChange={(e) => { setProviderFilter(e.target.value); setPage(0); }}
                            size="small"
                            fullWidth
                            sx={compactFilterSx}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {providerFilterOptions.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </TableCell>
                        <TableCell sx={filterCellSx} />
                        <TableCell sx={filterCellSx}>
                          <TextField
                            select
                            value={statusFilter}
                            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                            size="small"
                            fullWidth
                            sx={compactFilterSx}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {statusFilterOptions.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </TableCell>
                        <TableCell sx={filterCellSx}>
                          <LayeredAutocompleteSelect
                            label=""
                            value={customerFilter}
                            onChange={value => { setCustomerFilter(value); setPage(0); }}
                            size="small"
                            fullWidth
                            sx={compactFilterSx}
                            onClick={(e) => e.stopPropagation()}
                            inputAriaLabel="Filter devices by customer"
                            disableClearable
                            options={customerFilterOptions}
                          />
                        </TableCell>
                        <TableCell sx={filterCellSx}>
                          <TextField
                            value={userFilter}
                            onChange={(e) => setUserFilter(e.target.value)}
                            size="small"
                            placeholder="Filter…"
                            fullWidth
                            sx={compactFilterSx}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </TableCell>
                        <TableCell sx={filterCellSx} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {devices.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={9} align="center" sx={{ py: 4, color: "text.secondary" }}>
                            No devices match the current filters.
                          </TableCell>
                        </TableRow>
                      ) : (
                        devices.map((row) => {
                          const expanded = expandedDeviceId === row.device_key;
                          return (
                            <React.Fragment key={row.device_key}>
                              <TableRow
                                hover
                                onClick={() => void handleToggleDeviceDetails(row)}
                                sx={{
                                  cursor: "pointer",
                                  bgcolor: expanded ? "#F8FAFC" : "inherit",
                                  "& > td": { borderBottom: expanded ? "none" : "1px solid #E2E8F0" },
                                }}
                              >
                                <TableCell sx={{ width: 48 }}>
                                  <Tooltip title={expanded ? "Collapse" : "Expand"}>
                                    <IconButton
                                      size="small"
                                      aria-label={expanded ? "Collapse device details" : "Expand device details"}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        void handleToggleDeviceDetails(row);
                                      }}
                                    >
                                      {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                                <TableCell>{row.host_name || "—"}</TableCell>
                                <TableCell>
                                  <DeviceOsIcon osFamily={row.os_family} operatingSystem={row.operating_system} />
                                </TableCell>
                                <TableCell>
                                  <Chip size="small" label={providerDisplayName(row.provider)} variant="outlined" />
                                </TableCell>
                                <TableCell>{formatLastSeen(row.last_seen_ms)}</TableCell>
                                <TableCell>
                                  {row.online === null ? (
                                    String(row.provider || "").toLowerCase() === "intune" || row.compliance_state ? (
                                      <Chip
                                        size="small"
                                        label={formatComplianceLabel(row.compliance_state)}
                                        color={complianceChipColor(row.compliance_state)}
                                        variant="outlined"
                                      />
                                    ) : (
                                      "—"
                                    )
                                  ) : (
                                    <Chip
                                      size="small"
                                      label={onlineStatusLabel(row.online)}
                                      color={onlineStatusColor(row.online)}
                                      variant={row.online === false ? "outlined" : "filled"}
                                    />
                                  )}
                                </TableCell>
                                <TableCell>{customerDisplayName(row)}</TableCell>
                                <TableCell>{deviceUserDisplay(row)}</TableCell>
                                <TableCell align="right">
                                  <Button
                                    component={Link}
                                    href={buildDevicePath(row)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    size="small"
                                    variant="contained"
                                    startIcon={<AutoAwesomeIcon fontSize="small" />}
                                    endIcon={<OpenInNewIcon fontSize="small" />}
                                    onClick={(event) => event.stopPropagation()}
                                    sx={{ textTransform: "none", fontWeight: 700, whiteSpace: "nowrap" }}
                                  >
                                    Theo
                                  </Button>
                                </TableCell>
                              </TableRow>
                              {expanded && <TableRow>
                                <TableCell colSpan={9} sx={{ p: 0, borderBottom: "1px solid #CBD5E1" }}>
                                  <Collapse in={expanded} timeout="auto" unmountOnExit>
                                    {renderDeviceDetailPanel(row)}
                                  </Collapse>
                                </TableCell>
                              </TableRow>}
                            </React.Fragment>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination component="div" count={filteredCount} page={page} rowsPerPage={pageSize}
                  rowsPerPageOptions={[25, 50, 100]}
                  onPageChange={(_, next) => setPage(next)}
                  onRowsPerPageChange={event => { setPageSize(Number(event.target.value)); setPage(0); }} />
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default RmmDevices;
